import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

import { usePhaseReference } from "@/api/local/phases";
import { Pill } from "@/components/Pill";

/**
 * `/reference/phases` — operator-facing catalogue of every Pool /
 * Replica state the reconciler can drive a workload into.
 *
 * This page is the single source of truth the WebUI's PhaseBadge
 * components deep-link into via `#pool-<slug>` / `#replica-<slug>`
 * anchors. The data comes from `GET /reference/phases`; the
 * controller assembles it from the descriptors in
 * `controller/controller/reconciler_state.py` so the strings on
 * this page and the strings the reconciler logs stay in lockstep.
 *
 * Layout: three sections.
 *  1. Pool phases — full table with auto-recover flag, terminal
 *     flag, and the next-phase hints.
 *  2. Replica phases — same shape minus the recovery flag (replica
 *     phase transitions are entirely driven by SkyPilot's status
 *     enum + our terminal-failure overrides).
 *  3. Reconciler tuning constants — surfaced so operators reading
 *     this page can answer "how long before auto-reset kicks in?"
 *     without grepping the source.
 */
export function PhasesReference() {
  const ref = usePhaseReference();
  const location = useLocation();

  // Scroll-into-view for the deep-link anchors the PhaseBadge
  // generates. React Router won't scroll for hash-only navigation
  // by default; we do it here once the data has loaded so the
  // target row exists in the DOM.
  useEffect(() => {
    if (!location.hash || !ref.data) return;
    const id = location.hash.slice(1);
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      el.classList.add("ref-row--highlight");
      const t = window.setTimeout(
        () => el.classList.remove("ref-row--highlight"),
        2000,
      );
      return () => window.clearTimeout(t);
    }
  }, [location.hash, ref.data]);

  if (ref.isLoading) {
    return <div className="ref-page muted">Loading reference…</div>;
  }
  if (!ref.data) {
    return (
      <div className="ref-page muted">
        Unable to load `/reference/phases`. The controller may be
        unreachable.
      </div>
    );
  }
  const { pool_phases, replica_phases, tuning } = ref.data;

  return (
    <div className="ref-page">
      <header className="ref-page__header">
        <Link to="/" className="ref-page__back">
          <ArrowLeft aria-hidden />
          Back to Workloads
        </Link>
        <h1>Phases reference</h1>
        <p className="muted">
          Operator-facing state catalogue for Pools and Replicas. The
          reconciler writes one of these values onto every Pool /
          Replica row each tick; the WebUI's PhaseBadge looks up the
          matching label and tone here. Adding a new phase on the
          controller side propagates automatically.
        </p>
      </header>

      <section>
        <h2>Pool phases</h2>
        <div className="table-wrap">
          <table className="data ref-table">
            <thead>
              <tr>
                <th>Phase</th>
                <th>Auto-recovers</th>
                <th>Description</th>
                <th>Likely next phases</th>
              </tr>
            </thead>
            <tbody>
              {pool_phases.map((p) => (
                <tr key={p.slug} id={`pool-${p.slug}`} className="ref-row">
                  <td>
                    <div className="ref-row__label">
                      <Pill tone={p.tone}>{p.label}</Pill>
                      <code className="muted">{p.slug}</code>
                    </div>
                  </td>
                  <td>
                    {p.auto_recovers ? (
                      <span className="ok">yes</span>
                    ) : (
                      <span className="muted">no</span>
                    )}
                  </td>
                  <td className="ref-row__desc">
                    {p.description}
                    {p.next_action_label && (
                      <div className="muted" style={{ marginTop: 4 }}>
                        Operator: {p.next_action_label}
                      </div>
                    )}
                  </td>
                  <td>
                    {p.next_phases.length === 0 ? (
                      <span className="muted">—</span>
                    ) : (
                      <span style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                        {p.next_phases.map((n) => (
                          <a
                            key={n}
                            href={`#pool-${n}`}
                            className="ref-row__next"
                          >
                            {n}
                          </a>
                        ))}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h2>Replica phases</h2>
        <p className="muted" style={{ marginTop: 4 }}>
          Happy-path progression:{" "}
          <code>queued → provisioning → setup → healthy → draining → shutting_down</code>.
          Branches off the path: <code>unhealthy</code>, <code>failed</code>,{" "}
          <code>reaped</code>.
        </p>
        <div className="table-wrap">
          <table className="data ref-table">
            <thead>
              <tr>
                <th>Phase</th>
                <th>Step</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              {replica_phases.map((p) => (
                <tr key={p.slug} id={`replica-${p.slug}`} className="ref-row">
                  <td>
                    <div className="ref-row__label">
                      <Pill tone={p.tone}>{p.label}</Pill>
                      <code className="muted">{p.slug}</code>
                      {p.is_terminal && (
                        <span
                          className="ref-row__terminal"
                          title="Terminal phase"
                        >
                          terminal
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="mono">
                    {p.progress_index === null
                      ? "branch"
                      : `${p.progress_index + 1}/${p.progress_total}`}
                  </td>
                  <td className="ref-row__desc">{p.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h2>Reconciler tuning</h2>
        <p className="muted" style={{ marginTop: 4 }}>
          Constants the reconciler applies to auto-recovery and
          backoff. Defined in{" "}
          <code>controller/controller/reconciler_state.py</code>;
          change them there and redeploy to tune.
        </p>
        <div className="table-wrap">
          <table className="data ref-table">
            <tbody>
              <tr>
                <th>Status-lost reset threshold</th>
                <td className="num">
                  {tuning.status_lost_reset_ticks} consecutive ticks
                </td>
              </tr>
              <tr>
                <th>Retry backoff (transient errors)</th>
                <td className="num">
                  start {tuning.retry_backoff_base_seconds}s,
                  max {tuning.retry_backoff_max_seconds}s
                  (doubles per attempt — no cap; retries forever)
                </td>
              </tr>
              <tr>
                <th>Needs-attention probe interval</th>
                <td className="num">
                  {Math.round(
                    tuning.attention_probe_interval_seconds / 60,
                  )}{" "}
                  min (external_recoverable only)
                </td>
              </tr>
              <tr>
                <th>Retrying-persistent notify after</th>
                <td className="num">
                  {Math.round(
                    tuning.retrying_persistent_notify_seconds / 60,
                  )}{" "}
                  min in retrying
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
